// HTTP client for the FairMatch backend.
//
// `api()` reads bearer tokens from the global `state` and clears the session
// on 401. ApiError carries server-side error code + status so callers can
// branch (e.g., MIXED_DOUBLES_INFEASIBLE_IN_TEAM, INVITE_TOKEN_INVALID).

import { state, DEFAULT_CONFIG, persist } from "../state";

export class ApiError extends Error {
  code: string;
  status: number;
  details: any[];

  constructor(message: string, code: string, status: number, details?: any[]) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details || [];
  }
}

export function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export interface ApiOptions {
  method?: string;
  body?: unknown;
}

export async function api(path: string, options: ApiOptions = {}): Promise<any> {
  const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
  const url = base + path;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (state.session?.access_token) {
    headers.Authorization = `Bearer ${state.session.access_token}`;
  }
  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method || "GET",
      headers,
      body: options.body != null ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new ApiError("서버에 연결할 수 없어요", "NETWORK_ERROR", 0);
  }
  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    if (response.status === 401 && state.session) {
      state.session = null;
      persist();
    }
    const err = (payload && (payload.error || payload)) || {};
    throw new ApiError(
      err.message || `요청 실패 (${response.status})`,
      err.code || `HTTP_${response.status}`,
      response.status,
      err.details,
    );
  }
  if (payload && typeof payload === "object" && "data" in payload) return payload.data;
  return payload;
}
